import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const LAST_UPDATED = "28 October 2026";

// Each section renders as a numbered heading with its paragraphs / bullet lists.
// Strings are paragraphs; arrays are bullet lists.
const sections = [
  {
    id: "acceptance",
    title: "About these Terms",
    body: [
      "These Terms and Conditions (\"Terms\") govern your use of the ikusasatech.com website and every product, training course, programme and service offered by Ikusasa Technology Solutions (\"Ikusasa\", \"we\", \"us\" or \"our\"), a company registered in the Republic of South Africa with its offices at 299 Pine Avenue, Ferndale, Randburg, 2196.",
      "By browsing this website, submitting any form, enrolling in a course, requesting a quotation or engaging our services, you agree to be bound by these Terms. If you do not agree, please do not use the website or our services.",
      "Where you accept these Terms on behalf of a company or another person (for example, an employer booking staff training or a parent enrolling a child), you confirm that you have the authority to bind them.",
    ],
  },
  {
    id: "website",
    title: "Use of the Website",
    body: [
      "You may use this website for lawful purposes only. You agree not to:",
      [
        "submit false, misleading or another person's information in any form;",
        "attempt to gain unauthorised access to the website, its servers or any connected system;",
        "upload or transmit viruses, malicious code or spam;",
        "copy, scrape or republish website content for commercial purposes without our written permission.",
      ],
      "Course outlines, schedules, prices and other information on the website are provided for general guidance and may change without notice. While we take care to keep information accurate, we do not warrant that it is complete, current or error-free. A booking is only confirmed once you receive written confirmation from us.",
    ],
  },
  {
    id: "enrolment",
    title: "Course Applications and Enrolment",
    body: [
      "Submitting an application or enrolment form is a request for a place and does not guarantee admission. Places are allocated once the applicant meets the course entry requirements and payment (or an approved purchase order) has been received.",
      "You must provide complete and accurate information, including qualifications and contact details. We may decline or cancel an enrolment that is based on false information.",
      "Some programmes, including learnerships and sponsored or SETA-funded programmes, have additional eligibility criteria and agreements. Where those agreements apply, they take precedence over these Terms to the extent of any conflict.",
    ],
  },
  {
    id: "fees",
    title: "Fees and Payment",
    body: [
      [
        "All prices are quoted in South African Rand (ZAR) and include VAT unless stated otherwise.",
        "Quotations are valid for 30 days from the date of issue unless stated otherwise on the quotation.",
        "Course fees are payable in full before the course start date, unless we have agreed payment terms with you in writing.",
        "Corporate clients may book using an official purchase order. Invoices are payable within 30 days of the invoice date unless otherwise agreed.",
        "We may withhold course material, exam vouchers, certificates or access to learning platforms until fees are paid in full.",
        "Exam vouchers and vendor certification fees are set by the relevant vendor (for example Microsoft, CompTIA, Cisco or EC-Council) and may change outside our control.",
      ],
    ],
  },
  {
    id: "cancellations",
    title: "Cancellations, Refunds and Rescheduling",
    body: [
      "If you cancel a confirmed booking, the following applies unless a specific programme states otherwise:",
      [
        "Cancellation 7 or more days before the start date: full refund.",
        "Cancellation within 7 days of the start date: a 50% administration fee is charged and the balance is refunded.",
        "Once a course or programme has commenced, no refund is given.",
        "Exam vouchers, licences and third-party materials that have already been issued or activated are non-refundable.",
      ],
      "Cancellations must be sent in writing to info@ikusasatech.com. Instead of cancelling, you may transfer your place to another person or move to a later date of the same course, once and at no charge, provided you notify us at least 7 days before the start date.",
      "We may postpone or cancel a course if minimum numbers are not met or for reasons beyond our control. In that case you may choose a new date or a full refund of fees paid to us for that course. We are not liable for travel, accommodation or other costs you incur.",
      "Where you entered into the agreement as a result of direct marketing, you may cancel within 5 business days in terms of section 16 of the Consumer Protection Act, 68 of 2008, without penalty. Refunds are processed within 30 days of approval.",
    ],
  },
  {
    id: "attendance",
    title: "Attendance, Conduct and Certification",
    body: [
      [
        "Learners are expected to attend all sessions punctually and complete the required assessments. Certificates of completion are issued only where the attendance and assessment requirements of the course have been met.",
        "Missed sessions are not refunded. We will try to accommodate catch-up sessions where possible, but cannot guarantee this.",
        "Learners must treat facilitators, staff and other learners with respect and use our facilities and equipment with care. We may remove a learner from a course, without refund, for disruptive, abusive, unsafe or dishonest conduct, including cheating in assessments.",
        "You are responsible for any damage you cause to our equipment or premises, other than fair wear and tear.",
        "Vendor certification exams are written subject to the vendor's own rules. Passing a course does not guarantee passing a vendor exam, and we do not guarantee employment or placement after any course.",
      ],
    ],
  },
  {
    id: "minors",
    title: "Children's Programmes",
    body: [
      "For programmes for learners under 18, including Kids Coding and Future Engineers, the parent or legal guardian who enrols the child accepts these Terms on the child's behalf and confirms that the information provided, including any medical or dietary information, is accurate.",
      [
        "Parents or guardians must drop off and collect children at the agreed times. Please notify us in advance if someone else will be collecting your child.",
        "We take reasonable care to supervise children during sessions but are not responsible for children before drop-off or after the session ends.",
        "We may take photographs or videos during sessions for programme records and marketing. If you do not want your child to appear in published material, please tell us in writing when you enrol.",
      ],
    ],
  },
  {
    id: "services",
    title: "Consulting, Software and Product Services",
    body: [
      "Consulting, software development, hosting, support, cybersecurity and other professional services are delivered according to the scope, deliverables and timelines in the quotation, proposal or service agreement we agree with you. Where such a document exists, it forms part of these Terms and prevails if there is any conflict.",
      [
        "Changes to the agreed scope may affect the price and timeline and will be quoted separately.",
        "Hardware and software products are subject to availability and to the manufacturer's warranty. We will assist with warranty claims but the manufacturer's terms apply.",
        "You are responsible for providing timely access, information and approvals needed for us to deliver the services. Delays on your side may affect delivery dates.",
      ],
    ],
  },
  {
    id: "careers",
    title: "Job Applications",
    body: [
      "When you apply for a vacancy through this website, you confirm that the information and CV you submit are true and accurate. Submitting an application does not create any obligation on us to interview or employ you. We will use your information only to assess your application and, with your consent, to consider you for future vacancies, and will keep it for no longer than 12 months unless you are appointed.",
    ],
  },
  {
    id: "ip",
    title: "Intellectual Property",
    body: [
      "All content on this website and all course material, slides, workbooks, recordings, assessments and software we provide are owned by Ikusasa or our licensors and are protected by copyright and other intellectual property laws.",
      "Course material is licensed to you for your personal learning only. You may not copy, record, share, resell or distribute it without our written consent. Vendor names, logos and trademarks belong to their respective owners.",
    ],
  },
  {
    id: "privacy",
    title: "Privacy and Personal Information",
    body: [
      "We process personal information in line with the Protection of Personal Information Act, 4 of 2013 (POPIA). We collect the information you provide in our forms (such as your name, contact details, qualifications, CV and, for children's programmes, the child's details) in order to respond to enquiries, process applications and enrolments, deliver training and services, issue certificates and register exams with vendors.",
      [
        "We share your information only where needed to deliver our services, for example with certification vendors, examination bodies, SETAs or funders of sponsored programmes, and with service providers who host our systems, all of whom must protect it.",
        "We do not sell your personal information.",
        "We may send you information about our courses and services. You can opt out at any time by contacting us or using the unsubscribe option in our communications.",
        "You may ask to access, correct or delete your personal information, or object to its processing, by emailing info@ikusasatech.com. You also have the right to lodge a complaint with the Information Regulator (inforegulator.org.za).",
      ],
      "This website uses Google reCAPTCHA to protect our forms from spam, which is subject to Google's Privacy Policy and Terms of Service.",
    ],
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    body: [
      "To the extent permitted by law, including the Consumer Protection Act, we are not liable for any indirect, consequential or special loss or damage, including loss of profit, income, data or opportunity, arising from your use of the website or our services.",
      "Our total liability to you in connection with any course or service is limited to the fees you paid us for that course or service. Nothing in these Terms limits liability that cannot be limited by law, including liability for gross negligence.",
      "We are not responsible for the content of external websites linked from this site, or for delays or failures caused by events beyond our reasonable control, such as load-shedding, network outages, natural disasters, strikes or acts of government.",
    ],
  },
  {
    id: "general",
    title: "General",
    body: [
      [
        "These Terms are governed by the laws of the Republic of South Africa, and you agree to the jurisdiction of the South African courts.",
        "We may update these Terms from time to time. The version published on this page, with its \"last updated\" date, applies from the date of publication. Bookings already confirmed remain subject to the Terms in force when they were confirmed.",
        "If any part of these Terms is found to be invalid, the rest remains in force.",
        "Our failure to enforce any provision is not a waiver of our right to do so later.",
        "Notices to us must be in writing and sent to info@ikusasatech.com. Information required by section 43 of the Electronic Communications and Transactions Act, 25 of 2002, is available on request.",
      ],
    ],
  },
];

const Terms = () => {
  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <Helmet>
        <title>Terms and Conditions | Ikusasa Technology Solutions</title>
        <meta
          name="description"
          content="Terms and conditions for the Ikusasa Technology Solutions website, training courses, children's programmes and IT services, including fees, cancellations, refunds and privacy."
        />
      </Helmet>

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-[#016188] text-white rounded-2xl px-8 py-7 mb-8">
          <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-1">
            Legal
          </p>
          <h1 className="text-2xl md:text-3xl font-extrabold leading-tight">
            Terms and Conditions
          </h1>
          <p className="text-blue-100 text-sm mt-2">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
          {/* Contents */}
          <nav className="hidden md:block">
            <div className="sticky top-32 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h2 className="text-xs font-extrabold text-gray-500 uppercase tracking-wide mb-3">
                Contents
              </h2>
              <ol className="space-y-2 text-sm">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-gray-600 hover:text-[#016188] transition-colors duration-300"
                    >
                      {i + 1}. {s.title}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#contact"
                    className="text-gray-600 hover:text-[#016188] transition-colors duration-300"
                  >
                    {sections.length + 1}. Contact Us
                  </a>
                </li>
              </ol>
            </div>
          </nav>

          {/* Body */}
          <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 space-y-8 text-gray-700 text-sm leading-relaxed">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-32">
                <h2 className="text-lg font-extrabold text-[#016188] mb-3">
                  {i + 1}. {s.title}
                </h2>
                <div className="space-y-3">
                  {s.body.map((block, j) =>
                    Array.isArray(block) ? (
                      <ul key={j} className="list-disc pl-5 space-y-2">
                        {block.map((item, k) => (
                          <li key={k}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p key={j}>{block}</p>
                    )
                  )}
                </div>
              </section>
            ))}

            <section id="contact" className="scroll-mt-32">
              <h2 className="text-lg font-extrabold text-[#016188] mb-3">
                {sections.length + 1}. Contact Us
              </h2>
              <p className="mb-3">
                If you have any questions about these Terms, please{" "}
                <Link to="/contact" className="text-[#016188] font-semibold hover:underline">
                  contact us
                </Link>
                :
              </p>
              <ul className="space-y-1">
                <li>
                  <span className="font-semibold">Ikusasa Technology Solutions</span>
                </li>
                <li>299 Pine Avenue, Ferndale, Randburg, 2196</li>
                <li>Tel: 010 824 1153/54</li>
                <li>
                  Email:{" "}
                  <a href="mailto:info@ikusasatech.com" className="text-[#016188] hover:underline">
                    info@ikusasatech.com
                  </a>
                </li>
              </ul>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
};

export default Terms;
